import {
  $convertFromMarkdownString,
  MultilineElementTransformer,
  Transformer,
} from '@lexical/markdown';
import { $createParagraphNode, $getRoot, ElementNode } from 'lexical';

const CODE_START_REGEX = /^([ \t]*`{3,})([\w-]+)?[ \t]?/;
const CODE_END_REGEX = /^[ \t]*`{3,}$/;

function parseAndAppend(
  markdown: string,
  transformers: Transformer[],
  targetNode: ElementNode,
): void {
  const temp = $createParagraphNode();
  $getRoot().append(temp);
  $convertFromMarkdownString(markdown, transformers, temp, true);
  for (const child of temp.getChildren()) {
    targetNode.append(child);
  }
  temp.remove();
}

export const createCodePassthroughTransformer = (
  innerTransformers: Transformer[],
): MultilineElementTransformer => ({
  type: 'multiline-element',
  dependencies: [],

  regExpStart: CODE_START_REGEX,
  regExpEnd: {
    optional: true,
    regExp: CODE_END_REGEX,
  },

  // Returns [handled, lastConsumedLineIndex] so Lexical's import loop
  // knows to skip the lines we processed and resume after them.
  handleImportAfterStartMatch: ({ lines, rootNode, startLineIndex, startMatch }) => {
    const fence = startMatch[1];
    const fenceLength = fence.trim().length;
    const currentLine = lines[startLineIndex];
    const afterFence = currentLine.slice((startMatch.index ?? 0) + fence.length);

    const singleLineEndRegex = new RegExp(`^(.*?)\`{${fenceLength},}$`);
    const singleLineMatch = afterFence.match(singleLineEndRegex);
    if (singleLineMatch) {
      parseAndAppend(singleLineMatch[1], innerTransformers, rootNode);
      return [true, startLineIndex];
    }

    const closingFenceRegex = new RegExp(`^[ \\t]*\`{${fenceLength},}$`);
    const closingLineIndex = lines.findIndex(
      (line, i) => i > startLineIndex && closingFenceRegex.test(line),
    );

    const contentEnd = closingLineIndex >= 0 ? closingLineIndex : undefined;
    const linesInBetween = lines.slice(startLineIndex + 1, contentEnd);
    const afterFullMatch = currentLine.slice(startMatch[0].length);
    if (afterFullMatch.length > 0) {
      linesInBetween.unshift(afterFullMatch);
    }
    parseAndAppend(linesInBetween.join('\n'), innerTransformers, rootNode);

    const lastConsumedLine = closingLineIndex >= 0 ? closingLineIndex : lines.length - 1;
    return [true, lastConsumedLine];
  },

  // Intentionally a no-op: code blocks are always unwrapped on import so
  // there are no CodeNodes to export.
  export: () => null,

  replace: () => {},
});
